import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminServicesService } from '../services/admin.services.service'
import type { ServiceDocument } from '@/types'
import type { ServiceFormValues } from '../types'

interface UseAdminServicesResult {
  services: Array<ServiceDocument & { id: string }>
  loading: boolean
  error: string | null
  createService: (data: ServiceFormValues) => Promise<string>
  updateService: (id: string, data: Partial<ServiceFormValues>) => Promise<void>
  deleteService: (id: string) => Promise<void>
  refresh: () => void
}

export function useAdminServices(): UseAdminServicesResult {
  const [services, setServices] = useState<Array<ServiceDocument & { id: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => {
    setTick((t) => t + 1)
  }, [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminServicesService
      .getAllServices()
      .then(setServices)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load services'))
      )
      .finally(() => setLoading(false))
  }, [tick])

  const createService = useCallback(async (data: ServiceFormValues): Promise<string> => {
    const id = await adminServicesService.createService(data)
    refresh()
    return id
  }, [refresh])

  const updateService = useCallback(
    async (id: string, data: Partial<ServiceFormValues>): Promise<void> => {
      await adminServicesService.updateService(id, data)
      refresh()
    },
    [refresh]
  )

  const deleteService = useCallback(async (id: string): Promise<void> => {
    await adminServicesService.deleteService(id)
    refresh()
  }, [refresh])

  return { services, loading, error, createService, updateService, deleteService, refresh }
}
